import React, { Fragment, useEffect, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { Filter } from '../../components/_WMS/FilterComponent'
import { WorkOrderRecallSearch } from './WorkOrderRecallSearch'
import { getOrderRecallList } from './api'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import {
  GenericOrderBy,
  GenericTable,
  getNewDirection
} from '../../components/_EOS/Table'
import { WorkOrderRecall } from './entities/WorkOrderRecall'
import { defaultFilters } from './config'
import { usePagination } from '../../hooks/usePagination'
import { useWorkOrderState } from '../../hooks'
import { WorkOrderRecallButtonBar } from './WorkOrderRecallButtonBar'
import { Status } from '../../hooks/useFetchState'
import { IconButton, Tooltip } from '@mui/material'
import {
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  Delete as DeleteIcon
} from '@mui/icons-material'
import { generateTableHeadersWorkOrderRecall } from '.'
import { useModal } from '../../hooks/useModal'
import { eWorkOrderState } from '../../enums/eWorkOrderState'
import { EditWorkOrderRecallModal } from './modals/EditWorkOrderRecallModal'
import { Modal } from '../../components/_EOS/Modal'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { ViewWorkOrderRecallModal } from './modals/ViewWorkOrderRecallModal'
import { CreateWorkOrderRecallModal } from './modals/CreateWorkOrderRecallModal'
import { DeleteWorkOrderModal } from '../WorkOrderOutbound/modal/DeleteWorkOrderModal'

interface WorkOrderRecallPageProps {}

export const WorkOrderRecallPage: React.FC<WorkOrderRecallPageProps> = () => {
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()
  const { state, callApi } = useApi<ApiResponse<WorkOrderRecall>>()
  const [selectedElement, setSelectedElement] = React.useState<number[]>([])
  const [selectedFilters, setSelectedFilters] =
    React.useState<Filter[]>(defaultFilters)
  const [orderBy, setOrderBy] = useState<GenericOrderBy<WorkOrderRecall>>({
    field: '',
    direction: 'desc'
  })

  const pagination = usePagination(50)

  const {
    workOrderStatesState: { workOrderStates },
    getWorkOrderStates
  } = useWorkOrderState()

  React.useEffect(() => {
    if (workOrderStates?.length === 0) {
      getWorkOrderStates()
    }
  }, [])

  const tableHeadersWorkOrderRecall = generateTableHeadersWorkOrderRecall(
    workOrderStates || []
  )

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    setSelectedElement([])

    requestWorkOrderRecall(selectedFilters, auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  const handleCheckChange = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedElement((prev) => [...prev, id])
    } else {
      setSelectedElement((prev) => [...prev.filter((el) => el !== id)])
    }
  }

  const handleCheckAllToggle = (selectAll: boolean) => {
    if (selectAll) {
      // Selecciona todos los IDs de las filas actualmente visibles
      const newCheckedElements =
        state.data?.items.map((item) => item.workOrderId) || []
      setSelectedElement(newCheckedElements)
    } else {
      // Limpia la selección
      setSelectedElement([])
    }
  }

  const handleSorting = ({
    field,
    direction
  }: GenericOrderBy<WorkOrderRecall>) => {
    const auxField = field === 'workOrderId' ? 'workOrderId' : field
    if (auxField === orderBy.field) {
      const newDirection = getNewDirection(orderBy.direction)
      setOrderBy((_prev) => ({ field: auxField, direction: newDirection }))
      return
    }

    setOrderBy({ field, direction })
  }

  const requestWorkOrderRecall = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    callApi(getOrderRecallList(filters, page, pageSize, orderBy)).then(() =>
      setSelectedElement([])
    )
  }

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)

    requestWorkOrderRecall(selectedFilters, 0, pagination.pageSize)
  }, [orderBy, selectedFilters])

  type CustomActionsProps = {
    id: number
    row: WorkOrderRecall
  }

  const editModalState = useModal<{
    entity: WorkOrderRecall
  }>('')

  const viewModalState = useModal<{
    id: number
  }>('')

  const deleteModalState = useModal<{
    workOrderId: number
  }>('')
  const createModalState = useModal('Crear Orden')

  const RecallCustomActionsComponent: React.FC<CustomActionsProps> = ({
    row
  }) => {
    return (
      <Fragment>
        <Tooltip title='Detalle' aria-label='edit'>
          <IconButton
            aria-label='seeBinacle'
            onClick={() =>
              viewModalState.openModal(
                'md',
                { id: row.workOrderId },
                `Bitácora Orden # ${row.workOrderId}`
              )
            }
            style={{ verticalAlign: 'bottom' }}
          >
            <AssignmentIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title='Editar Orden' aria-label='edit'>
          <IconButton
            aria-label='seeBinacle'
            onClick={() =>
              editModalState.openModal('md', { entity: row }, 'Editar Orden')
            }
            style={{ verticalAlign: 'bottom' }}
            disabled={row.workOrderStateId === eWorkOrderState.FINISHED}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title='Eliminar Orden' aria-label='edit'>
          <IconButton
            aria-label='seeBinacle'
            onClick={() =>
              deleteModalState.openModal(
                'md',
                { workOrderId: row.workOrderId },
                'Eliminar Orden'
              )
            }
            style={{ verticalAlign: 'bottom' }}
            disabled={row.workOrderStateId !== eWorkOrderState.CREATED}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <StandardPage pageTitle='Administrador Orden de Retiro'>
        {/* SEARCH */}
        <WorkOrderRecallSearch
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={requestWorkOrderRecall}
        />
        {/* BUTTON BAR */}
        <WorkOrderRecallButtonBar
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          handleSearch={requestWorkOrderRecall}
          workOrderStates={workOrderStates}
          onClickCreateWorkOrderRecall={createModalState.openModal}
        />

        {/* TABLE */}
        <GenericTable<WorkOrderRecall>
          headersConfig={tableHeadersWorkOrderRecall}
          IDKey={'workOrderId'}
          data={state.data?.items}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          customActionComponent={RecallCustomActionsComponent}
          onPaginationChange={handlePaginationChange}
          isLoading={state.status === Status.Loading}
          showCheckboxes={true}
          onCheckChange={handleCheckChange}
          checkedElements={selectedElement}
          onSortChange={handleSorting}
          onCheckAll={handleCheckAllToggle}
          orderField={orderBy}
          priorizationNumberKey='rowNum'
        />
      </StandardPage>
      <Modal
        open={viewModalState.isOpen}
        title={viewModalState.title}
        maxWidth={viewModalState.size}
        onClose={viewModalState.closeModal}
      >
        <ViewWorkOrderRecallModal
          workOrderId={viewModalState.extendedProps?.id as number}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <Modal
        open={createModalState.isOpen}
        title={createModalState.title}
        maxWidth={createModalState.size}
        onClose={createModalState.closeModal}
        cancelButton={{
          action: createModalState.closeModal,
          title: 'Salir'
        }}
      >
        <CreateWorkOrderRecallModal
          handleSnackbar={openSnackBar}
          handleCloseModal={createModalState.closeModal}
          requestWorkOrderRecall={() => {
            requestWorkOrderRecall(
              selectedFilters,
              pagination.page,
              pagination.pageSize
            )
          }}
        />
      </Modal>

      <Modal
        open={editModalState.isOpen}
        title={editModalState.title}
        maxWidth={editModalState.size}
        onClose={editModalState.closeModal}
        cancelButton={{
          action: editModalState.closeModal,
          title: 'Salir'
        }}
      >
        <EditWorkOrderRecallModal
          workOrderRecall={
            editModalState.extendedProps?.entity as WorkOrderRecall
          }
          handleSnackbar={openSnackBar}
          handleCloseModal={editModalState.closeModal}
          requestWorkOrderRecall={() => {
            requestWorkOrderRecall(
              selectedFilters,
              pagination.page,
              pagination.pageSize
            )
          }}
        />
      </Modal>

      <DeleteWorkOrderModal
        deleteWorkOrderModalState={deleteModalState}
        handleSnackbarNotification={openSnackBar}
        handleRequestWorkOrders={() =>
          requestWorkOrderRecall(
            selectedFilters,
            pagination.page,
            pagination.pageSize
          )
        }
      />

      {/* SNACKBAR */}
      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
